import React, { useState, useEffect } from 'react'; // Import useState and useEffect
import { useParams, useNavigate } from 'react-router-dom';

import { adminApi } from '../../services/api'; // Import adminApi
import AuditLogs from '../../components/shared/AuditLogs';

const CaseAuditLogsPage = ({ caseId: propCaseId }) => {
  const { caseId: paramCaseId } = useParams();
  const caseId = propCaseId || paramCaseId;

  // State for case details and audio sessions
  const [caseDetails, setCaseDetails] = useState(null);
  const [audioSessions, setAudioSessions] = useState([]);
  const [loadingCase, setLoadingCase] = useState(true);
  const [loadingAudio, setLoadingAudio] = useState(true);
  const [caseError, setCaseError] = useState(null);
  const [audioError, setAudioError] = useState(null);

  // Handle audio file download with audit logging
  const handleDownloadAudio = async (sessionId, downloadType, segmentNumber = null) => {
    setAudioError(null);
    
    try {
      const downloadData = {
        case_id : caseId,
        session_id: sessionId,
        download_type: downloadType
      };      
      if (segmentNumber !== null) {
        downloadData.segment_number = segmentNumber;
      }      
      const response = await adminApi.downloadAudioFile(downloadData);      
      if (response.data && response.data.success && response.data.download_url) {
        // Open the presigned URL in a new tab
        window.open(response.data.download_url, '_blank');
      } else {
        // console.error('Failed to get download URL:', response.data);
        const errorMessage = response.data?.error || 'Failed to generate download link';
        setAudioError(errorMessage);
      }
    } catch (error) {
      // console.error('Download error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error downloading audio file';
      setAudioError(errorMessage);
    }
  };

  // Fetch case details
  useEffect(() => {
    const fetchCaseDetails = async () => {
      try {
        setLoadingCase(true);
        const response = await adminApi.getCaseDetails(caseId);
        if (response.data && response.data.success) {
          setCaseDetails(response.data.case); // Assuming the response contains a 'case' object
        } else {
          setCaseError(response.data?.message || 'Failed to fetch case details');
        }
      } catch (err) {
        // console.error('Error fetching case details:', err);
        setCaseError(err.message || 'An error occurred while fetching case details');
      } finally {
        setLoadingCase(false);
      }
    };

    if (caseId) {
      fetchCaseDetails();
    }
  }, [caseId]); // Refetch when caseId changes


  // Fetch audio sessions
  useEffect(() => {
    const fetchAudioSessions = async () => {
      try {
        setLoadingAudio(true);
        const response = await adminApi.getCaseAudioSessions(caseId);
        if (response.data && response.data.sessions) {
          setAudioSessions(response.data.sessions);
        } else {
          setAudioError('Failed to fetch audio sessions');
        }
      } catch (err) {
        // console.error('Error fetching audio sessions:', err);
        setAudioError(err.message || 'An error occurred while fetching audio sessions');
      } finally {
        setLoadingAudio(false);
      }
    };

    if (caseId) {
      fetchAudioSessions();
    }
  }, [caseId]);

  const navigate = useNavigate();

  const handleBackToList = () => {
    navigate(-1);
  };

  return (
    <>
      <div>
        {!caseId && (<h5 className="card-title mb-3">
        <button
          className="btn btn-sm btn-light me-2"
          onClick={handleBackToList}
        >
          <i className="fa fa-arrow-left me-1"></i>
        </button> Case Details for {caseId}</h5>)}
        {/* Case Details */}
        <div className="card mb-4">
          <div className="card-body">
          <div className="card-title text-primary">Case Details</div>
            {loadingCase ? (
              <p>Loading case details...</p>
            ) : caseError ? (
              <div className="alert alert-danger">{caseError}</div>
            ) : caseDetails ? (
              <div>
                <p><strong>MiraSol Case ID:</strong> {caseId}</p>
                <p><strong>Core Server Case ID:</strong> {caseDetails.CoreServer_CaseID}</p>
                <p><strong>Created on:</strong> {new Date(caseDetails.created_at).toLocaleString([], { timeZoneName: 'short' })}</p>
                <p><strong>Patient:</strong> {caseDetails.patient_info_fk ? `${caseDetails.patient_info_fk.first_name} ${caseDetails.patient_info_fk.last_name} (DOB: ${new Date(caseDetails.patient_info_fk.dob).toString() !== 'Invalid Date' ? new Date(caseDetails.patient_info_fk.dob).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }) : caseDetails.patient_info_fk.dob} | Core Server ID: ${caseDetails.patient_info_fk.patient_id})` : 'N/A'}</p>
                <p><strong>Provider:</strong> {caseDetails.provider_info_fk ? `${caseDetails.provider_info_fk.first_name} ${caseDetails.provider_info_fk.last_name} (Core Server ID: ${caseDetails.provider_info_fk.provider_id})` : 'N/A'}</p> 
                <p><strong>Health System:</strong> {caseDetails.healthcare_info_fk ? caseDetails.healthcare_info_fk.name : 'N/A'}</p> 
                {caseDetails.latest_zoom_meeting ? (
                  <div>
                    <p style={{marginTop:'15px',fontWeight:'600'}}>VIDEO CONFERENCE DETAILS</p>
                    <p><strong>Topic:</strong> {caseDetails.latest_zoom_meeting.topic}</p>
                    <p><strong>Passcode:</strong> {caseDetails.latest_zoom_meeting.passcode}</p>
                    <p><strong>Created at:</strong> {new Date(caseDetails.latest_zoom_meeting.created_at).toLocaleString([], { timeZoneName: 'short' })}</p>
                  </div>
                ) : (
                  <p className='text-muted'><strong>Video visit:</strong> Details not found <i className="fas fa-info-circle" title="Did you request a video visit link? Check audit trail"></i></p>
                )}
              </div>
            ) : (
              <p>Case details not found.</p>
            )}
          </div>
        </div>

        {/* Audit Logs Table */}
        <div className="card mt-4">
          <div className="card-body">
            <AuditLogs
              resourceId={caseId}
              resourceType="case"
              title="Case Audit Trail"
              showHeader={true}
            />
          </div>
        </div>

      </div>
    </>
  );
};

export default CaseAuditLogsPage;